import React, { useCallback, useState } from 'react';
import { Box, Dialog, DialogProps } from '@mui/material';
import styled from 'styled-components';
import {
  Row,
  SPWBaseCheckbox,
  SPWCancelButton,
  SPWMainButton,
} from 'spw-shared/globalComponents';
import {
  getProjectEditors,
  setCurrentProject,
  transferProjectOwnership,
} from 'spw-redux/modules/projects/actions';
import moment from 'moment';
import { currentUser as selectCurrentUser } from 'spw-redux/modules/user/selectors';
import { getAriaLabelForSensitiveInfo } from 'spw-shared/utils';
import { SearchInput } from 'spw-shared/globalComponents/input';
import { USER_SEARCH_PLACEHOLDER_TEXT } from 'spw-constants';
import { FormControlLabel } from 'spw-components/dialogs/styles';
import { colors } from 'spw-theme';
import { IProject, UserRoleInProject } from 'spw-redux/modules/projects/types';
import { IUser } from 'spw-redux/modules/user/types';
import { useDispatch, useSelector } from 'react-redux';
import { FormChangeHandler } from '../types';
import { useUserSuggestions } from '../users/hooks';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 289px;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 24px;

  .title {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 24px;
  }
  .directions {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 8px;
  }
  .expanded {
    flex: 1;
  }
  .footer {
    .MuiButton-root {
      box-sizing: border-box;

      &:first-child {
        margin-right: 16px;
        width: 195px;
      }
      &:nth-child(2) {
        width: 88;
      }
    }
  }
  .search-input {
    margin-bottom: 24px;
    width: 100%;
  }
  .checkbox-container {
    .MuiCheckbox-root {
      margin-right: 8px;
    }
    .MuiFormControlLabel-root {
      margin-left: unset;
    }
  }
`;

type TransferOwnershipForm = {
  newProjectOwner: IUser;
  preservePreviousOwnerAsEditor: boolean;
};

type Props = {
  isVisible: boolean;
  onClose: () => void;
  project?: IProject | null;
  onTransferSuccess?: (
    projectId: number,
    preservePreviousOwnerAsEditor: boolean,
    newProjectOwner: IUser
  ) => void;
};

const EMPTY_FORM: TransferOwnershipForm = {
  newProjectOwner: null,
  preservePreviousOwnerAsEditor: false,
};

function TransferProjectOwnershipDialog({
  isVisible,
  onClose,
  project,
  onTransferSuccess,
}: Props) {
  const [form, setForm] = useState<TransferOwnershipForm>(EMPTY_FORM);
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const userSuggestions = useUserSuggestions(searchText);
  const currentUser = useSelector(selectCurrentUser);

  const handleChange = useCallback<FormChangeHandler<TransferOwnershipForm>>(
    (key, value) => {
      setForm((prevValue) => ({
        ...prevValue,
        [key]: value,
      }));
    },
    [setForm]
  );

  const closeDialog = useCallback(() => {
    setForm(EMPTY_FORM);
    setSearchText('');
    if (onClose) onClose();
  }, [onClose]);

  const handleClose = useCallback<DialogProps['onClose']>(
    (_, reason) => {
      if (reason !== 'backdropClick') {
        closeDialog();
      }
    },
    [closeDialog]
  );

  const getNewRoleInProjectForCurrentUser = () => {
    if (
      form.preservePreviousOwnerAsEditor &&
      project.currentUserRole === UserRoleInProject.projectOwner
    ) {
      return UserRoleInProject.editor;
    }
    if (project.currentUserRole === UserRoleInProject.projectOwner)
      return UserRoleInProject.viewer;
    return project.currentUserRole;
  };

  const handleOwnershipTransferredSuccessfully = () => {
    setForm(EMPTY_FORM);
    setSearchText('');

    if (onTransferSuccess)
      return onTransferSuccess(
        project.id,
        form.preservePreviousOwnerAsEditor,
        form.newProjectOwner
      );

    dispatch(
      setCurrentProject({
        ...project,
        projectOwner: form.newProjectOwner,
        currentUserRole: getNewRoleInProjectForCurrentUser(),
        lastEditor: currentUser,
        updatedAt: moment().format(),
      })
    );
    return dispatch(
      getProjectEditors({
        projectId: project.id,
        sort: { nameOwnerFirst: 'ASC' },
      })
    );
  };

  const save = () => {
    onClose();
    dispatch(
      transferProjectOwnership({
        user: form.newProjectOwner,
        projectId: project.id,
        preservePreviousOwnerAsEditor: form.preservePreviousOwnerAsEditor,
        onSuccess: handleOwnershipTransferredSuccessfully,
      })
    );
  };

  const context = 'Transfer project ownership dialog';

  return (
    <Dialog open={isVisible} onClose={handleClose} disableScrollLock>
      <Container>
        <div className="expanded">
          <div className="title">Transfer Project Ownership</div>
          <div className="directions">
            Transfer the project from{' '}
            <b
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Project Owner Name',
                id: project?.projectOwner?.id,
              })}
            >
              {project?.projectOwner?.name}
            </b>{' '}
            to:
          </div>
          <SearchInput
            id="user_search_input"
            freeSolo
            options={userSuggestions.items}
            loading={userSuggestions.areLoading}
            onChange={(newValue) => {
              setSearchText(
                typeof newValue === 'string' ? newValue : newValue.name || ''
              );
              if (newValue.name) {
                handleChange('newProjectOwner', newValue);
              }
            }}
            onDelete={() => {
              setSearchText('');
              handleChange('newProjectOwner', null);
            }}
            getOptionLabel={(option: IUser) => option.name || option.toString()}
            renderOption={(props, user) => (
              <Box
                component="li"
                aria-label={getAriaLabelForSensitiveInfo({
                  context,
                  desc: 'Search User Option',
                  id: user.id,
                })}
                {...props}
              >
                {user.name}
              </Box>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionDisabled={(option: IUser) =>
              option.id === project?.projectOwner?.id
            }
            className="search-input"
            textFieldProps={{
              fullWidth: true,
              placeholder: USER_SEARCH_PLACEHOLDER_TEXT,
              onChange: ({ target: { value } }) => {
                setSearchText(value);
              },
            }}
            value={form.newProjectOwner}
            inputValue={searchText}
          />
          <Row alignItems="center" className="checkbox-container">
            <FormControlLabel
              disabled={!project?.projectOwner?.isActive}
              control={
                <SPWBaseCheckbox
                  disabled={!project?.projectOwner?.isActive}
                  checked={form.preservePreviousOwnerAsEditor}
                  onChange={(e) =>
                    handleChange(
                      'preservePreviousOwnerAsEditor',
                      e.target.checked
                    )
                  }
                />
              }
              label={
                <>
                  Keep{' '}
                  <b
                    aria-label={getAriaLabelForSensitiveInfo({
                      context,
                      desc: 'Project Owner Name',
                      id: project?.projectOwner?.id,
                    })}
                  >
                    {project?.projectOwner?.name}
                  </b>{' '}
                  as a Project Editor.
                </>
              }
            />
          </Row>
        </div>
        <Row className="footer">
          <SPWMainButton
            className="margin-right-16"
            disabled={!form.newProjectOwner}
            onClick={save}
          >
            TRANSFER OWNERSHIP
          </SPWMainButton>
          <SPWCancelButton customColor={colors.black} onClick={closeDialog} />
        </Row>
      </Container>
    </Dialog>
  );
}

export default TransferProjectOwnershipDialog;
